/**************************************************************************************************/
/** @import Classes/Records                                                                      **/
/**************************************************************************************************/
import { ActionItem } from '@/Classes/Records/ActionItem'
import FieldsOptions from '@/Classes/Records/FieldsOptions'
import { ItemMissingFields } from '@/Classes/Records/ItemMissingFields'
import { RecordHelper } from '@/Classes/Records/RecordHelper'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { ActionFields } from '@/Constants/ActionFields'
import { AppModules } from '@/Constants/AppModules'
import { AppValues } from '@/Constants/AppValues'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { ActionField } from '@/Helpers/Components/DataTable/ActionField'
import { BuilderManager } from '@/Helpers/Components/DataTable/BuilderManager'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @internal Const @name actionFieldStatus                                                      **/
/**************************************************************************************************/
const actionFieldStatus = new ActionField('status', 'Estado')

/**************************************************************************************************/
/** @export Function @name parseAddonsResponse                                                   **/
/**************************************************************************************************/
export function parseAddonsResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data)

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idCheckListAddons', '_idStorages', 'storages', 'isForClient')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('code', 'Código').isSortable().setAlign(null, 'center')
		.add('name', 'Descripción').isSortable()
		.add('typeEquipment', 'Tipo Equipo').isSortable().setAlign(null, 'center')
		.add('groupArticle', 'Grupo Artículo').isSortable().setAlign(null, 'center')
		.add('attributes', 'Preguntas')
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('code', 'name', 'typeEquipment', 'groupArticle', 'attributes', 'isForClient')

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		attributes: (values: Array<string>) =>
		{
			return values.sort()
		},

		groupArticle: (value: string) =>
		{
			return value === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY
				? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY.toUpperCase()
				: value
		},

		isForClient: (value: boolean) =>
		{
			return value ? 'Sí' : 'No'
		}
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseStackedAddonsResponse                                            **/
/**************************************************************************************************/
export function parseStackedAddonsResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data)

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idCheckListAddons', 'storages', 'attributes', 'groupArticle', 'isForClient', 'name', 'typeEquipment')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('code', 'Código').isSortable().setAlign(null, 'center').showDetailsButton()
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('code')

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		code: (value: string, item: any) =>
		{
			return `${ value } (${ item.name })`
		}
	})

	// Definir los campos a mostrar en el Detalle.
	bm.parseItemsDetail({
		[RecordHelper.setFlags('Descripción', 'name')]: ({ name }) =>
		{
			return name
		},
		[RecordHelper.setFlags('Tipo Equipo', 'typeEquipment')]: ({ typeEquipment }) =>
		{
			return typeEquipment
		},
		[RecordHelper.setFlags('Grupo Artículo', 'groupArticle')]: ({ groupArticle }) =>
		{
			return groupArticle === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY
				? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY.toUpperCase()
				: groupArticle
		},
		['Preguntas']: ({ attributes }) =>
		{
			return attributes
		},
		[RecordHelper.setFlags('Exclusivo Cliente', 'isForClient')]: ({ isForClient }) =>
		{
			return isForClient ? 'Sí' : 'No'
		}
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseCheckListsResponse                                               **/
/**************************************************************************************************/
export function parseCheckListsResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data, [...ActionFields.SearchPDF, actionFieldStatus])

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idChecklist', '_idCheckListAddons', '_idStorage', '_idOperator', '_idEquipment', 'attributes', 'dataListAddons', 'comment', 'dataSetting', 'companyName', 'storageName', 'dataLocation')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('codeEquipment', 'Código Equipo').isSortable().setAlign('center', 'center')
		.add('nameEquipment', 'Nombre Equipo').isSortable().setAlign('center', 'center').setSearchKey('equip.name')
		.add('groupArticle', 'Grupo Artículo').isSortable().setAlign('center', 'center').setSearchKey('addons.groupArticle')
		.add('hourmeter', 'Horómetro').setAlign('center', 'center')
		.add('addonsCode', 'Cód. Formulario').isSortable().setAlign('center', 'center').setSearchKey('addons.code')
		.add('addonsName', 'Nombre Formulario').isSortable().setAlign('center', 'center').setSearchKey('addons.name')
		.add('operator', 'Responsable').isSortable().setAlign('center', 'center').setSearchKey('user.name')
		.add('zone', 'Sector').isSortable().setAlign('center', 'center').setSearchKey('zone.code')
		.add('date', 'Fecha').setAlign('center', 'center')
		.add('ok', 'OK').setAlign('center', 'center').setStyleTh({ width: '64px' })
		.add('not', 'NO').setAlign('center', 'center').setStyleTh({ width: '64px' })
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('codeEquipment', 'nameEquipment', 'hourmeter', 'groupArticle', 'addonsCode', 'addonsName', 'operator', 'zone', 'date', 'ok', 'not', 'status')

	// Aplicar un valor a los campos e items si no estan presentes.
	bm.parseItemsMissingFields(new ItemMissingFields()
		.add('addonsName')
		.add('addonsCode')
		.add('groupArticle')
		.add('nameEquipment')
		.add('ok')
		.add('not')
		.add('zone')
	)

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		addonsCode: function(v: never, item: any)
		{
			const { dataListAddons } = item
			if (Array.isArray(dataListAddons) && dataListAddons.length === 0) return AppValues.Strings.DEFAULT_EMPTY_STRING
			const [addons] = dataListAddons
			return addons ? addons.code : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		addonsName: (v: never, item: any) =>
		{
			const { dataListAddons } = item
			if (Array.isArray(dataListAddons) && dataListAddons.length === 0) return AppValues.Strings.DEFAULT_EMPTY_STRING
			const [addons] = dataListAddons
			return addons ? addons.name : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		codeEquipment: (v: string) =>
		{
			return v || AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		date: (v: string) =>
		{
			const _date = TypesHelper.toDatePartsFromString(v)
			const date  = _date ? `${ _date.date } ${ _date.time }` : ''
			return date || AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		groupArticle: (_: void, item: any) =>
		{
			const [addonsList] = item.dataListAddons
			return addonsList?.groupArticle === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY
				? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY
				: addonsList?.groupArticle || AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		hourmeter: (v: string) =>
		{
			return v || AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		not: (_: void, item: any) =>
		{
			const { attributes } = item
			return attributes ? attributes?.filter((x: any) => !x.status).length : 0
		},

		ok: (_: void, item: any) =>
		{
			const { attributes } = item
			return attributes ? attributes?.filter((x: any) => x.status).length : 0
		},

		zone: (_: void, item: any) =>
		{
			const { dataLocation } = item
			if (!dataLocation || dataLocation?._idZone === null) return 'SIN SECTOR'
			if (dataLocation?.zoneCode === undefined && dataLocation?.zoneName === undefined) return 'SIN SECTOR'
			return dataLocation.zoneCode
		}
	})

	// Realizar una Ejecución para cada Item.
	bm.forEachItem((item: any) =>
	{
		const { attributes, not, status } = item
		const numCriticalBad = attributes ? attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : x.critical)).length : 0
		const numNonCriticalBad = attributes ? attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : !x.critical)).length : 0

		// El icono de la columna 'status' debe ser agregada despues que los valores de las columnas OK y NO fueron calculados.
		const isOK = not === 0
		const notPresent = not === -1
		const hasOneCriticalBad = numCriticalBad > 0
		const hasNonCriticalBad = numNonCriticalBad > 0

		let icon = 'minus-circle'
		let variant = 'gray'

		if (hasOneCriticalBad)
		{
			icon = 'times-circle'
			variant = 'red'
		}
		else if (hasNonCriticalBad)
		{
			icon = 'exclamation-circle'
			variant = 'janssen-yellow'
		}
		else if (isOK)
		{
			icon = 'check-circle'
			variant = 'green'
		}

		// Definición del icono de la columna.
		const text = isOK ? 'OK' : notPresent ? AppValues.Strings.DEFAULT_EMPTY_STRING : 'NO'
		const actionItem = new ActionItem('status', 'icon').setIcon(icon).setPermission('ACTION_STATUS').setText(text).setVariant(variant)
		status.push(actionItem)
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseStackedCheckListsResponse                                        **/
/**************************************************************************************************/
export function parseStackedCheckListsResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data, [actionFieldStatus])

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idChecklist', '_idCheckListAddons', '_idEquipment', '_idOperator', '_idStorage', 'attributes', 'comment', 'dataLocation', 'dataListAddons', 'date', 'hourmeter', 'nameEquipment', 'operator', 'dataSetting', 'companyName', 'storageName')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('codeEquipment', 'Código Equipo').isSortable().setAlign(null, 'center').showDetailsButton()
	)

	// Aplicar un valor a los campos e items si no estan presentes.
	bm.parseItemsMissingFields(new ItemMissingFields()
		.add('ok')
		.add('not')
	)

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		codeEquipment: (value: string, item: any) =>
		{
			const [addons] = item.dataListAddons
			const typeEquipment = addons?.typeEquipment === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY ? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY.toUpperCase() : addons?.typeEquipment
			return typeEquipment && addons?.groupArticle ? `${ value } - ${ addons?.groupArticle } (${ typeEquipment })` : value
		},

		date: (v: string) =>
		{
			const _date = TypesHelper.toDatePartsFromString(v)
			const date = _date ? `${ _date.date } ${ _date.time }` : ''
			return date || AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		not: (_: void, item: any) =>
		{
			const { attributes } = item
			return attributes ? attributes?.filter((x: any) => !x.status).length : 0
		},

		ok: (_: void, item: any) =>
		{
			const { attributes } = item
			return attributes ? attributes?.filter((x: any) => x.status).length : 0
		}
	})

	// Realizar una Ejecución para cada Item.
	bm.forEachItem((item: any) =>
	{
		const { attributes, not, status } = item
		const numCriticalBad = attributes ? attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : x.critical)).length : 0
		const numNonCriticalBad = attributes ? attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : !x.critical)).length : 0

		// El icono de la columna 'status' debe ser agregada despues que los valores de las columnas OK y NO fueron calculados.
		const isOK = not === 0
		const notPresent = not === -1
		const hasOneCriticalBad = numCriticalBad > 0
		const hasNonCriticalBad = numNonCriticalBad > 0

		let icon = 'minus-circle'
		let variant = 'gray'

		if (hasOneCriticalBad)
		{
			icon = 'times-circle'
			variant = 'red'
		}
		else if (hasNonCriticalBad)
		{
			icon = 'exclamation-circle'
			variant = 'janssen-yellow'
		}
		else if (isOK)
		{
			icon = 'check-circle'
			variant = 'green'
		}

		// Definición del icono de la columna.
		const text = isOK ? 'OK' : notPresent ? AppValues.Strings.DEFAULT_EMPTY_STRING : 'NO'
		const actionItem = new ActionItem('status', 'icon').setIcon(icon).setPermission('ACTION_STATUS').setText(text).setVariant(variant)
		status.push(actionItem)
	})

	// Definir los campos a mostrar en el Detalle.
	bm.parseItemsDetail({
		[RecordHelper.setFlags('Nombre Equipo', 'nameEquipment', 'equip.name')]: ({ nameEquipment }) =>
		{
			return nameEquipment || AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Grupo Artículo', 'groupArticle', 'addons.groupArticle')]: ({ dataListAddons }) =>
		{
			const [addonsList] = dataListAddons
			return addonsList?.groupArticle === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY
				? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY
				: addonsList?.groupArticle || AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		['Horómetro']: ({ hourmeter }) =>
		{
			if (hourmeter === null) return AppValues.Strings.DEFAULT_EMPTY_STRING
			return !isNaN(hourmeter) ? `${ hourmeter } Hrs.` : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Cód. Formulario', 'addonsCode', 'addons.code')]: ({ dataListAddons }) =>
		{
			if (Array.isArray(dataListAddons) && dataListAddons.length === 0) return AppValues.Strings.DEFAULT_EMPTY_STRING
			const [addons] = dataListAddons
			return addons ? addons.code : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Nombre Formulario', 'addonsName', 'addons.name')]: ({ dataListAddons }) =>
		{
			if (Array.isArray(dataListAddons) && dataListAddons.length === 0) return AppValues.Strings.DEFAULT_EMPTY_STRING
			const [addons] = dataListAddons
			return addons ? addons.name : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Responsable', 'operator', 'user.name')]: ({ operator }) =>
		{
			return operator
		},
		[RecordHelper.setFlags('Sector', 'zone')]: ({ dataLocation }) =>
		{
			if (!dataLocation || dataLocation?._idZone === null) return 'SIN SECTOR'
			if (dataLocation?.zoneCode === undefined && dataLocation?.zoneName === undefined) return 'SIN SECTOR'
			return dataLocation.zoneCode
		},
		[RecordHelper.setFlags('Fecha', 'date')]: ({ date }) =>
		{
			return date
		},
		['OK']: ({ ok }) =>
		{
			return ok
		},
		['NO']: ({ not }) =>
		{
			return not
		},
		['Acciones']: ActionFields.SearchPDF
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseEquipmentsResponse                                               **/
/**************************************************************************************************/
export function parseEquipmentsResponse(data: Array<any>, showClient = false)
{
	// Clase Constructora.
	const bm = new BuilderManager(data, [...ActionFields.SearchPDF, actionFieldStatus])

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('storageName', 'Planta').isSortable().setAlign(null, 'center').setSearchKey('addons.code')
		.add('addonsCode', 'Cód. Formulario').isSortable().setAlign(null, 'center').setSearchKey('addons.code')
		.add('code', 'Código Activo').isSortable().setAlign(null, 'center').setSearchKey('equipment.code')
		.add('dateCheckList', 'Fecha').setAlign(null, 'center')
		.add('groupArticle', 'Grupo Articulo').isSortable().setAlign(null, 'center').setSearchKey('equipment.groupArticle')
		.add('name', 'Descripción').isSortable().setAlign(null, 'center')
		.add('operator', 'Responsable').isSortable().setAlign(null, 'center').setSearchKey('responsible.name')
		.add('typeEquipment', 'Tipo Equipo').isSortable().setAlign(null, 'center').setSearchKey('equipment.typeEquipment')
		.add('zoneCode', 'Cód. Sector').isSortable().setAlign(null, 'center')
		.add('zoneName', 'Nombre Sector').isSortable().setAlign(null, 'center')
		.add('ok', 'OK').setAlign('center', 'center').setStyleTh({ width: '64px' })
		.add('not', 'NO').setAlign('center', 'center').setStyleTh({ width: '64px' })
	)

	// Eliminar los campos que no queremos mostrar en la tabla.
	const removeFields = ['_idEquipment', 'checkList', 'companyName', 'dataListAddons', 'zoneLocation']
	if (!showClient) removeFields.push('storageName')
	bm.removeFields(...removeFields)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('storageName', 'code', 'name', 'groupArticle', 'typeEquipment', 'addonsCode', 'operator', 'dateCheckList', 'zoneCode', 'zoneName', 'ok', 'not', 'status')

	// Aplicar un valor a los campos e items si no estan presentes.
	bm.parseItemsMissingFields(new ItemMissingFields()
		.add('addonsCode')
		.add('dateCheckList')
		.add('operator')
		.add('ok')
		.add('not')
		.add('typeEquipment')
		.add('zoneCode')
		.add('zoneName')
	)

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		addonsCode: (_: void, item: any) =>
		{
			const { dataListAddons } = item
			if (Array.isArray(dataListAddons) && dataListAddons.length === 0) return AppValues.Strings.DEFAULT_EMPTY_STRING
			const [addons] = dataListAddons
			return addons ? addons.code : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		dateCheckList: (_: void, item: any) =>
		{
			const { checkList } = item
			const date  = checkList ? checkList.date : null
			const _date = TypesHelper.toDatePartsFromString(date)
			return _date ? `${ _date.date } ${ _date.time }` : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		typeEquipment: (value: string) =>
		{
			return value === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY
				? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY.toUpperCase()
				: value
		},

		ok: (_: void, item: any) =>
		{
			const { checkList } = item
			return checkList ? checkList.attributes?.filter((x: any) => x.status).length : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		not: (_: void, item: any) =>
		{
			const { checkList } = item
			return checkList ? checkList.attributes?.filter((x: any) => !x.status).length : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		zoneCode: (_: void, item: any) =>
		{
			const { zoneLocation } = item
			return zoneLocation[0]?.zoneCode || AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		zoneName: (_: void, item: any) =>
		{
			const { zoneLocation } = item
			return zoneLocation[0]?.zoneName || AppValues.Strings.DEFAULT_EMPTY_STRING
		}
	})

	// Realizar una Ejecución para cada Item.
	bm.forEachItem((item: any) =>
	{
		const { checkList, not, status } = item
		const numCriticalBad = checkList ? checkList.attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : x.critical)).length : 0
		const numNonCriticalBad = checkList ? checkList.attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : !x.critical)).length : 0

		// El icono de la columna 'status' debe ser agregada despues que los valores de las columnas OK y NO fueron calculados.
		const isOK = not === 0
		const notPresent = not === -1
		const hasOneCriticalBad = numCriticalBad > 0
		const hasNonCriticalBad = numNonCriticalBad > 0

		let icon = 'minus-circle'
		let variant = 'gray'

		if (hasOneCriticalBad)
		{
			icon = 'times-circle'
			variant = 'red'
		}
		else if (hasNonCriticalBad)
		{
			icon = 'exclamation-circle'
			variant = 'janssen-yellow'
		}
		else if (isOK)
		{
			icon = 'check-circle'
			variant = 'green'
		}

		// Definición del icono de la columna.
		const text = isOK ? 'OK' : notPresent ? AppValues.Strings.DEFAULT_EMPTY_STRING : 'NO'
		const actionItem = new ActionItem('status', 'icon').setIcon(icon).setPermission('ACTION_STATUS').setText(text).setVariant(variant)
		status.push(actionItem)
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseStackedEquipmentsResponse                                        **/
/**************************************************************************************************/
export function parseStackedEquipmentsResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data, [actionFieldStatus])

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idEquipment', 'checkList', 'operator', 'groupArticle', 'typeEquipment', 'companyName', 'storageName', 'dataListAddons', 'zoneLocation')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('code', 'Cód. Activo').isSortable().setAlign(null, 'center').setSearchKey('equipment.code').showDetailsButton()
		.add('name', 'Descripción').isSortable().setAlign(null, 'center')
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('code', 'name')

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		code: (value: string, item: any) =>
		{
			const typeEquipment = item.typeEquipment === AppModules.Modules.CheckLists.TypeActiveNames.FACILITY ? AppModules.Modules.CheckLists.TypeActiveTranslationNames.FACILITY.toUpperCase() : item.typeEquipment
			return `${ value } - ${ item.groupArticle } (${ typeEquipment })`
		}
	})

	// Realizar una Ejecución para cada Item.
	bm.forEachItem((item: any) =>
	{
		const { checkList, not, status } = item
		const numCriticalBad = checkList ? checkList.attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : x.critical)).length : 0
		const numNonCriticalBad = checkList ? checkList.attributes?.filter((x: any) => !x.status && (x.critical === undefined ? true : !x.critical)).length : 0

		// El icono de la columna 'status' debe ser agregada despues que los valores de las columnas OK y NO fueron calculados.
		const isOK = not === 0
		const notPresent = not === -1
		const hasOneCriticalBad = numCriticalBad > 0
		const hasNonCriticalBad = numNonCriticalBad > 0

		let icon = 'minus-circle'
		let variant = 'gray'

		if (hasOneCriticalBad)
		{
			icon = 'times-circle'
			variant = 'red'
		}
		else if (hasNonCriticalBad)
		{
			icon = 'exclamation-circle'
			variant = 'janssen-yellow'
		}
		else if (isOK)
		{
			icon = 'check-circle'
			variant = 'green'
		}

		// Definición del icono de la columna.
		const text = isOK ? 'OK' : notPresent ? AppValues.Strings.DEFAULT_EMPTY_STRING : 'NO'
		const actionItem = new ActionItem('status', 'icon').setIcon(icon).setPermission('ACTION_STATUS').setText(text).setVariant(variant)
		status.push(actionItem)
	})

	// Definir los campos a mostrar en el Detalle.
	bm.parseItemsDetail({
		[RecordHelper.setFlags('Grupo Artículo', 'groupArticle', 'equipment.groupArticle')]: ({ groupArticle }) =>
		{
			return groupArticle
		},
		[RecordHelper.setFlags('Tipo Equipo', 'typeEquipment', 'equipment.typeEquipment')]: ({ typeEquipment }) =>
		{
			return typeEquipment
		},
		[RecordHelper.setFlags('Cód. Formulario', 'addonsCode', 'addons.code')]: ({ dataListAddons }) =>
		{
			if (Array.isArray(dataListAddons) && dataListAddons.length === 0) return AppValues.Strings.DEFAULT_EMPTY_STRING
			const [addons] = dataListAddons
			return addons ? addons.code : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Responsable', 'operator', 'responsible.name')]: ({ operator }) =>
		{
			return operator
		},
		['Fecha']: ({ checkList }) =>
		{
			const date  = checkList ? checkList.date : null
			const _date = TypesHelper.toDatePartsFromString(date)
			return _date ? `${ _date.date } ${ _date.time }` : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		['OK']: ({ checkList }) =>
		{
			return checkList ? checkList.attributes?.filter((x: any) => x.status).length : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		['NO']: ({ checkList }) =>
		{
			return checkList ? checkList.attributes?.filter((x: any) => !x.status).length : AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		['Acciones']: ActionFields.SearchPDF
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}